import type { Nullish } from '@aigc/types';
import { array, number, object, string, type InferType } from 'yup';
import {
  GenerationType,
  videoModelSpecs,
  type VideoModelCode,
} from '../video-model/model-spec';

export const durationSchema = number()
  .nullable()
  .required()
  .when(
    ['model', '$generation_type', '$is_long_video_supported'],
    (values, schema, options) => {
      const totalDuration = options.value as Nullish<number>;

      if (totalDuration == null) {
        return schema;
      }

      const [model, generation_type, is_long_video_supported] = values as [
        VideoModelCode,
        GenerationType,
        boolean | undefined,
      ];
      const spec = videoModelSpecs.get(model);
      if (!spec) {
        return schema;
      }

      const { duration } = spec;

      let newSchema = schema;

      if (
        generation_type === GenerationType.Reference &&
        spec.long_video &&
        is_long_video_supported
      ) {
        return newSchema;
      }

      const modelName = videoModelSpecs.get(model)?.name ?? model;

      if (Array.isArray(duration)) {
        newSchema = newSchema.oneOf(
          [...duration],
          `${modelName} 要求总时长必须等于 ${duration.join(' 或 ')} 秒`,
        );
      } else {
        if (duration.min) {
          newSchema = newSchema.min(
            duration.min,
            `${modelName} 要求总时长不得小于 ${duration.min} 秒`,
          );
        }

        if (duration.max) {
          newSchema = newSchema.max(
            duration.max,
            ({ max }) => `${modelName} 限制总时长不得大于 ${max} 秒`,
          );
        }
      }

      return newSchema;
    },
  );

export const simplePromptSchema = string()
  .label('提示词')
  .when('$simple', {
    is: true,
    then: (s) => s.required(),
  })
  .when(['model', '$simple_mode'], (values, schema, options) => {
    const prompt = options.value as Nullish<string>;

    if (prompt == null) {
      return schema;
    }

    const [model, simpleMode] = values as [VideoModelCode, boolean | undefined];

    if (!simpleMode) {
      return schema;
    }

    const spec = videoModelSpecs.get(model);
    if (!spec) {
      return schema;
    }

    const { max_prompt } = spec;

    if (max_prompt) {
      schema = schema.max(max_prompt);
    }

    return schema;
  })
  .nullable();

const complexPromptSchema = object({
  shot_type: string().nullable(),
  shot_purpose: string().nullable(),
  duration: number().nullable().required(),
  visual_description: string().label('场景描述').nullable().required(),
  camera_movement: string().nullable(),
  dialogue_subtitle_emotion: string().nullable(),
  visual_keywords: array().of(string().required()).nullable(),
});

export const multiPromptSchema = array()
  .of(complexPromptSchema)
  .label('提示词')
  .when('$simple_mode', {
    is: (value: boolean | undefined) => value !== true,
    then: (s) => s.min(1).required('请输入提示词'),
  });

export type ComplexPromptFormValues = InferType<typeof complexPromptSchema>;
export type MultiPromptFormValues = InferType<typeof multiPromptSchema>;
